import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  Users,
  PlusCircle,
  Shield,
  Settings,
  Edit2,
  Trash2,
  Search,
  Lock,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  X,
  MoreVertical
} from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { getErrorMessage } from "@/utils/errorHandler";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { usePermission } from "@/contexts/RoleAccessContext";

/* -----------------------------
 * LABELS + FEATURES (same)
 * ---------------------------- */
export const FEATURE_LABELS = {
  payroll_analytics: { view: "View Payroll Analytics" }, //done
  activity_logs: { view: "View Activity Logs" }, //done

  employees: {
    view: "View Employees", //done
    create: "Create Employee", //done
    edit: "Edit Employee", //done
    delete: "Delete Employee", //done
  },

  employee_change_requests: {
    view: "View Profile Change Requests",
    edit: "Approve / Reject Profile Change Requests",
  },

  login_details: { view: "View Login Details" }, //done

  run_payroll: { view: "View Payroll Run", edit: "Run / Edit Payroll" }, //done
  salary_structure: { view: "View Salary Structure" }, //done

  payslips: {
    view: "View Payslips", //done
    generate_bank_advice: "Generate Bank Advice", //done
    email_send: "Email Payslips", //done
  },

  loan_management: {
    view: "View Loans", //done
    create: "Create Loan", //done
    edit: "Edit Loan", //done
  },

  reimbursement_management: {
    view: "View Reimbursement Claims",
    create: "Submit Claims for Employees",
    edit: "Approve / Reject / Pay / Cancel Claims",
  },

  regularization_requests: {
    view: "View Regularization Requests", //done
    edit: "Edit Regularization Requests", //done
  },

  shifts: {
    view: "View Shifts", //done
    create: "Create Shift", //done
    edit: "Edit Shift", //done
    delete: "Delete Shift", //done
  },

  rotational_shift_schedules: {
    view: "View Rotational Schedules", //done
    edit: "Edit Rotational Schedules", //done
  },

  overtime_management: {
    view: "View Overtime Requests", //done
    create: "Create Overtime Request", //done
    edit: "Edit Overtime Requests", //done
  },

  attendance_dashboard: {
    view: "View Attendance Dashboard",
  },

  attendance_reports: {
    view: "View Attendance Reports", //done
    create_late: "Create Late Entry", //done
  },

  leave_management: {
    view: "View Leave Requests", //done
    create: "Create Leave Request", //done
    edit: "Edit / Approve Leave", //done
  },

  holiday_configuration: {
    view: "View Holidays", //done
    create: "Create Holiday", //done
    edit: "Edit Holiday", //done
    delete: "Delete Holiday", //done
  },

  event_management: {
    view: "View Events", //done
    create: "Create Event", //done
    edit: "Edit Event", //done
    delete: "Delete Event", //done
  },

  tax_configuration: {
    view: "View Tax Configuration", //done
    create: "Create Tax Rule", //done
    edit: "Edit Tax Rule", //done
    delete: "Delete Tax Rule", //done
  },

  salary_components_configuration: {
    view: "View Salary Components", //done
    create: "Create Salary Component", //done
    edit: "Edit Salary Component", //done
    delete: "Delete Salary Component", //done
  },

  settings_company_configuration: {
    view: "View Company Settings", //done
    edit: "Edit Company Settings", //done
  },
  attendance_and_policy_configuration: {
    view: "View Policy Configuration", //done
    edit: "Edit Policy Configuration", //done
  },
  // settings_notification_configuration: {
  //   view: "View Notification Settings",
  //   edit: "Edit Notification Settings",
  // },
  // settings_security_configuration: {
  //   view: "View Security Settings", //done
  //   edit: "Edit Security Settings", //done
  // },
  settings_bank_configuration: {
    view: "View Bank Accounts", //done
    create: "Add Bank Account", //done
    edit: "Edit Bank Account", //done
    delete: "Delete Bank Account", //done
  },
  settings_loan_configuration: {
    view: "View Loan Settings", //done
    create: "Create Loan Setting", //done
    edit: "Edit Loan Setting", //done
    delete: "Delete Loan Setting", //done
  },
  settings_leave_configuration: {
    view: "View Leave Settings", //done
    create: "Create Leave Setting", //done
    edit: "Edit Leave Setting", //done
    delete: "Delete Leave Setting", //done
  },

  work_locations_configuration: {
    view: "View Work Locations", //done
    create: "Create Work Location", //done
    edit: "Edit Work Location", //done
    delete: "Delete Work Location", //done
  },
  department_configuration: {
    view: "View Departments", //done
    create: "Create Department", //done
    edit: "Edit Department", //done
    delete: "Delete Department", //done
  },
  designation_configuration: {
    view: "View Designations", //done
    create: "Create Designation", //done
    edit: "Edit Designation", //done
    delete: "Delete Designation", //done
  },
  roles_and_access: {
    view: "View Roles & Access", //done
    create: "Create Role", //done
    edit: "Edit Role", //done
    delete: "Delete Role", //done
  },
  subscription_management: {
    view: "View Subscription Details", //done
    edit: "Manage Subscription", //done
  },
  goals_okrs: {
    view: "View Goals & OKRs",
    create: "Create Goals", 
    edit: "Edit Goal", 
    delete: "Delete Goal", 
  },
  performance_management: {
    view: "View Reviews",
    create: "Create Review",
    delete: "Delete Review",
    review: "Review Details",
    approve: "Approve Review",
  },
  template_builder: {
    view: "View Templates",
    create: "Create Template",
    edit: "Edit Template",
    delete: "Delete Template",
  },
  company_documents: {
    view: "View Company Documents",
    create: "Upload Documents",
    edit: "Edit & Replace Documents",
    delete: "Archive & Delete Documents",
  },
}

export const FEATURES = {
  payroll_analytics: { view: true },
  activity_logs: { view: true },

  employees: { view: true, create: true, edit: true, delete: true },
  employee_change_requests: { view: true, edit: true },
  login_details: { view: true },

  run_payroll: { view: true, edit: true, delete: true },
  salary_structure: { view: true },

  payslips: { view: true, generate_bank_advice: true, email_send: true },
  loan_management: { view: true, create: true, edit: true },
  reimbursement_management: { view: true, create: true, edit: true },

  regularization_requests: { view: true, edit: true },
  shifts: { view: true, create: true, edit: true, delete: true },
  rotational_shift_schedules: { view: true, edit: true },
  overtime_management: { view: true, create: true, edit: true },
  attendance_dashboard: { view: true },
  attendance_reports: { view: true, create_late: true },

  leave_management: { view: true, create: true, edit: true },
  holiday_configuration: { view: true, create: true, edit: true, delete: true },

  event_management: { view: true, create: true, edit: true, delete: true },

  tax_configuration: { view: true, create: true, edit: true, delete: true },
  salary_components_configuration: {
    view: true,
    create: true,
    edit: true,
    delete: true,
  },

  settings_company_configuration: { view: true, edit: true },
  attendance_and_policy_configuration: { view: true, edit: true },
  // settings_notification_configuration: { view: true, edit: true },
  // settings_security_configuration: { view: true, edit: true },

  settings_bank_configuration: {
    view: true,
    create: true,
    edit: true,
    delete: true,
  },
  settings_loan_configuration: {
    view: true,
    create: true,
    edit: true,
    delete: true,
  },
  settings_leave_configuration: {
    view: true,
    create: true,
    edit: true,
    delete: true,
  },

  work_locations_configuration: {
    view: true,
    create: true,
    edit: true,
    delete: true,
  },
  department_configuration: {
    view: true,
    create: true,
    edit: true,
    delete: true,
  },
  designation_configuration: {
    view: true,
    create: true,
    edit: true,
    delete: true,
  },

  roles_and_access: { view: true, create: true, edit: true, delete: true },
  overtime_management: { view: true, create: true, edit: true },
  subscription_management: { view: true, edit: true },

  performance_management: { view: true, create: true, delete: true, review: true, approve: true },
  goals_okrs: { view: true, create: true, edit: true, delete: true },
  template_builder: { view: true, create: true, edit: true, delete: true },
  company_documents: { view: true, create: true, edit: true, delete: true },

};

/* -----------------------------
 * helpers
 * ---------------------------- */
function titleCaseFromKey(key) {
  return String(key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function countEnabled(featuresObj) {
  let count = 0;
  Object.values(featuresObj || {}).forEach((actions) => {
    Object.values(actions || {}).forEach((v) => v && count++);
  });
  return count;
}

export default function Permissions({
  roles = [],
  loading = false,
  onSaveRole,
  onDeleteRole,
  onRefresh,
}) {
  const [open, setOpen] = useState(false);

  const [roleName, setRoleName] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  const [features, setFeatures] = useState({});
  const [mode, setMode] = useState("create"); // "create" | "edit"
  const [editingRoleId, setEditingRoleId] = useState(null);

  const [search, setSearch] = useState("");

  // delete confirmation
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const { can } = usePermission();
  const canViewRoles = can("roles_and_access", "view");
  const canCreateRole = can("roles_and_access", "create");
  const canEditRole = can("roles_and_access", "edit");
  const canDeleteRole = can("roles_and_access", "delete");

  /* ---------------- TOGGLE FEATURE ---------------- */
  const toggleFeature = (feature, action) => {
    setFeatures((prev) => {
      const next = {
        ...prev,
        [feature]: {
          ...prev[feature],
          [action]: !prev?.[feature]?.[action],
        },
      };

      // If turning OFF view, also turn OFF all other actions.
      if (action === "view" && prev?.[feature]?.view === true) {
        Object.keys(next[feature] || {}).forEach((k) => {
          if (k !== "view") next[feature][k] = false;
        });
      }

      return next;
    });
  };

  const openCreate = () => {
    if (!canCreateRole) {
      toast.error("You do not have permission to create roles");
      return;
    }
    setMode("create");
    setEditingRoleId(null);
    setRoleName("");
    setRoleDescription("");
    setFeatures({});
    setOpen(true);
  };

  const handleEditRole = (role) => {
     if (!canEditRole) {
       toast.error("You do not have permission to edit roles");
       return;
     }
    setMode("edit");
    setEditingRoleId(role._id ?? role.id);
    setRoleName(role.name || "");
    setRoleDescription(role.description || "");
    setFeatures(role.features || {});
    setOpen(true);
  };

  const normalizeFeatures = (allFeatures, selectedFeatures) => {
    const normalized = {};
    Object.entries(allFeatures).forEach(([featureKey, actions]) => {
      normalized[featureKey] = {};
      Object.keys(actions).forEach((actionKey) => {
        normalized[featureKey][actionKey] = Boolean(
          selectedFeatures?.[featureKey]?.[actionKey],
        );
      });
    });
    return normalized;
  };
  const { allSelected, someSelected } = useMemo(() => {
    let total = 0;
    let enabled = 0;

    Object.entries(FEATURES).forEach(([featureKey, actions]) => {
      Object.keys(actions).forEach((actionKey) => {
        total += 1;
        if (features?.[featureKey]?.[actionKey]) enabled += 1;
      });
    });

    return {
      allSelected: total > 0 && enabled === total,
      someSelected: enabled > 0 && enabled < total,
    };
  }, [features]);

  const handleSelectAllChange = (v) => {
    const shouldSelectAll = v === true || v === "indeterminate";
    if (shouldSelectAll) selectAll();
    else clearAll();
  };

  const getFeatureSelectState = (featureKey, actions) => {
    const actionKeys = Object.keys(actions || {});
    const enabled = actionKeys.filter((k) =>
      Boolean(features?.[featureKey]?.[k]),
    ).length;

    return {
      total: actionKeys.length,
      enabled,
      allSelected: enabled > 0 && enabled === actionKeys.length,
      someSelected: enabled > 0 && enabled < actionKeys.length,
    };
  };

  const setAllForFeature = (featureKey, actions, nextValue) => {
    setFeatures((prev) => {
      const next = { ...prev, [featureKey]: { ...(prev?.[featureKey] || {}) } };

      Object.keys(actions || {}).forEach((actionKey) => {
        next[featureKey][actionKey] = Boolean(nextValue);
      });

      // IMPORTANT: if unselecting all, ensure view is false (and thus others false)
      // (already handled by setting everything false above)
      return next;
    });
  };

  const handleSaveRole = async () => {
    try {
      if (!roleName.trim()) {
        toast.error("Role name is required");
        return;
      }

      const hasAnyPermission = Object.values(features).some((actions) =>
        Object.values(actions || {}).some(Boolean),
      );

      if (!hasAnyPermission) {
        toast.error("Select at least one permission");
        return;
      }

      const payload = {
        name: roleName.trim(),
        description: roleDescription,
        features: normalizeFeatures(FEATURES, features),
      };

      if (mode === "edit") payload.id = editingRoleId;

      await onSaveRole(payload);
      await onRefresh();

      setOpen(false);
      setRoleName("");
      setRoleDescription("");
      setFeatures({});
      setMode("create");
      setEditingRoleId(null);

      toast.success(mode === "edit" ? "Role updated" : "Role created");
    } catch (error) {
      console.error("Failed to save role:", error);
      toast.error(error?.message || "Failed to save role");
    }
  };

  const roleCards = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = Array.isArray(roles) ? roles : [];
    if (!q) return list;

    return list.filter((r) => {
      const name = String(r?.name || "").toLowerCase();
      const desc = String(r?.description || "").toLowerCase();
      return name.includes(q) || desc.includes(q);
    });
  }, [roles, search]);

  const selectedCount = useMemo(() => countEnabled(features), [features]);

  // quick actions (modal)
  const selectAll = () => {
    const allTrue = {};
    Object.entries(FEATURES).forEach(([featureKey, actions]) => {
      allTrue[featureKey] = {};
      Object.keys(actions).forEach((actionKey) => {
        allTrue[featureKey][actionKey] = true;
      });
    });
    setFeatures(allTrue);
  };

  const clearAll = () => setFeatures({});

  const requestDeleteRole = (role) => {
    if (!canDeleteRole) {
      toast.error("You do not have permission to delete roles");
      return;
    }

    if (role?.is_default) {
      toast.error("Default role cannot be deleted");
      return;
    }
    setRoleToDelete(role);
    setDeleteOpen(true);
  };

  const confirmDeleteRole = async () => {
    if (!roleToDelete) return;

    const id = roleToDelete._id ?? roleToDelete.id;
    if (!id) {
      toast.error("Invalid role id");
      return;
    }

    try {
      setDeleting(true);
      await onDeleteRole(id);
      await onRefresh();
      toast.success("Role deleted successfully");
      setDeleteOpen(false);
      setRoleToDelete(null);
    } catch (error) {
      let errorMessage = getErrorMessage(error, "Failed to delete role");
      toast.error(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const clearSearch = () => setSearch("");

  if (!canViewRoles) {
    return (
      <Card className="p-8 text-center">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <Lock className="h-6 w-6" />
          <p className="font-semibold">Access Restricted</p>
          <p className="text-sm">
            You do not have permission to view Roles & Access.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header (modern + consistent with Users) */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div className="space-y-0.5">
            <h2 className="text-lg font-semibold text-foreground">
              Roles
            </h2>
            <p className="text-xs text-muted-foreground">
              Create roles and manage module permissions.
            </p>
          </div>

          <Button
            onClick={openCreate}
            className="gap-2 sm:self-auto self-start"
            disabled={!canCreateRole}
          >
            <PlusCircle className="h-4 w-4" />
            Add Role
          </Button>
        </div>

        {/* Search row */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="relative w-full md:w-[380px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search roles..."
              className="pl-9 pr-9"
            />
            {search?.trim() ? (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted text-muted-foreground"
                title="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>

          <div className="text-xs text-muted-foreground">
            {roleCards.length} role{roleCards.length === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      {/* Roles grid card */}
      <Card className="border-border/60 overflow-hidden">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            Roles
          </CardTitle>
          <CardDescription>
            Manage access control rules for your organization.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {roleCards.length === 0 ? (
            <div className="border border-dashed rounded-lg p-8 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                <Users className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="mt-3 font-semibold">No roles found</p>
              <p className="text-sm text-muted-foreground">
                Create a role and define what modules it can access.
              </p>
              <Button onClick={openCreate} className="mt-4 gap-2" disabled={!canCreateRole}>
                <PlusCircle className="h-4 w-4" />
                Add Role
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {roleCards.map((role) => {
                const roleId = role._id ?? role.id;
                const total = countEnabled(role.features || {});
                const isDefault = Boolean(role.is_default);

                const preview = Object.entries(role.features || {})
                  .flatMap(([feature, actions]) =>
                    Object.entries(actions || {})
                      .filter(([_, v]) => v)
                      .map(([action]) => {
                        return (
                          FEATURE_LABELS?.[feature]?.[action] ||
                          `${titleCaseFromKey(feature)} · ${titleCaseFromKey(action)}`
                        );
                      }),
                  )
                  .slice(0, 4);

                return (
                  <Card
                    key={String(roleId)}
                    className="
    group relative overflow-hidden
    border-border/60 bg-background
    transition-all
    hover:-translate-y-[1px] hover:border-primary/30 hover:shadow-md
  "
                  >
                    <div className="pointer-events-none absolute inset-0 opacity-100 transition-opacity">
                      <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full dark:bg-primary/10 blur-3xl" />
                      <div className="absolute -bottom-24 -left-24 h-56 w-56 rounded-full dark:bg-primary/5 blur-3xl" />
                    </div>

                    <CardHeader className="relative pb-3">
                      <div className="grid grid-cols-[1fr_auto] gap-3 items-start">
                        {/* LEFT */}
                        <div className="flex items-center gap-3 min-w-0">
                          <div className=" h-11 w-11 shrink-0 rounded-xl bg-primary/10 text-primary ring-1 ring-primary/15 flex items-center justify-center">
                            <Users className="h-5 w-5" />
                          </div>
                          <div className="min-w-0 w-full">
                            {/* Row 1: Title */}
                            <div className="flex items-center gap-2 min-w-0">
                              <h4 className="font-semibold text-foreground truncate">
                                {role.name}
                              </h4>

                              {isDefault && (
                                <Badge
                                  variant="secondary"
                                  className="gap-1 rounded-full px-2 py-0.5 text-[11px] shrink-0"
                                >
                                  <Lock className="h-3 w-3" />
                                  Default
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* RIGHT: 3-dot menu */}
                        {!isDefault && (canEditRole || canDeleteRole) && (
                            <div className="flex items-center">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    size="icon"
                                    variant="secondary"
                                    className="h-9 w-9 bg-background"
                                    title="Role actions"
                                  >
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>

                                <DropdownMenuContent
                                  align="end"
                                  className="bg-background w-44"
                                >
                                  <DropdownMenuItem
                                    onClick={() => handleEditRole(role)}
                                    disabled={isDefault || !canEditRole}
                                    className="gap-2"
                                  >
                                    <Edit2 className="h-4 w-4" />
                                    Edit role
                                  </DropdownMenuItem>

                                  <DropdownMenuItem
                                    onClick={() => requestDeleteRole(role)}
                                    disabled={isDefault || !canDeleteRole}
                                    className="gap-2 text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                    Delete role
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                        )}
                      </div>
                    </CardHeader>

                    <CardContent className="relative space-y-3">
                      {/* Chips */}
                      <div className="flex flex-wrap gap-2 min-h-[44px] content-start">
                        {preview.length === 0 ? (
                          <div className=" w-full rounded-lg border border-dashed border-border/70 bg-muted/30 px-3 py-2 text-xs italic text-muted-foreground">
                            No privileges assigned
                          </div>
                        ) : (
                          <>
                            {preview.map((label) => (
                              <Badge
                                key={label}
                                variant="secondary"
                                className=" max-w-full rounded-full bg-primary/10 text-primary dark:bg-primary/15"
                              >
                                <span className="truncate">{label}</span>
                              </Badge>
                            ))}

                            {total > 4 && (
                              <Badge
                                variant="outline"
                                className="rounded-full bg-transparent"
                              >
                                +{total - 4} more
                              </Badge>
                            )}
                          </>
                        )}
                      </div>

                      {/* Simple bottom info only (no actions) */}
                      <div className="pt-3 border-t border-border/60 flex items-center justify-between gap-3">
                        <span className="text-[11px] text-muted-foreground line-clamp-1">
                          {role.description ? role.description : ""}
                        </span>

                        <span className="text-[11px] text-muted-foreground">
                          Total:{" "}
                          <span className="font-semibold text-foreground">
                            {total}
                          </span>
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ---------------- CREATE / EDIT ROLE DIALOG ---------------- */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[980px] flex flex-col max-h-[92vh] p-0">
          <DialogHeader className="p-6 border-b">
            <div className="flex items-start justify-between gap-4">
              <div>
                <DialogTitle className="text-lg md:text-xl font-bold flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  {mode === "edit" ? "Edit Role" : "Create Role"}
                </DialogTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  {mode === "edit"
                    ? "Update role details and permissions."
                    : "Create a role and define what it can access."}
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="flex flex-col px-6 overflow-hidden gap-5 max-h-[90vh] overflow-y-auto">
            {/* Role Details */}
            <div className="space-y-4">
              {/* Row 1: Name + Description in same line */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                {/* Role Name */}
                <div className="space-y-2 min-w-0">
                  <Label htmlFor="role-name" className="text-sm font-medium">
                    Role Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="role-name"
                    placeholder="e.g., HR Manager, Payroll Executive"
                    value={roleName}
                    onChange={(e) => setRoleName(e.target.value)}
                    className="w-full"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Keep it short and consistent (e.g., “Payroll Executive”).
                  </p>
                </div>

                {/* Role Description */}
                <div className="space-y-2 min-w-0">
                  <Label
                    htmlFor="role-description"
                    className="text-sm font-medium"
                  >
                    Role Description <span className="text-red-500">*</span>
                  </Label>

                  <Textarea
                    id="role-description"
                    placeholder="Describe what this role can access and what it's responsible for..."
                    value={roleDescription || ""}
                    onChange={(e) => setRoleDescription(e.target.value)}
                    className="min-h-[120px] resize-none w-full"
                  />
                </div>
              </div>
            </div>

            {/* Permissions */}
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Shield className="h-4 w-4 text-primary" />
                Permissions by Module
              </h3>
              <p className="text-xs text-muted-foreground">
                Tip: Enable <b>View</b> first to unlock other actions.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Checkbox
                checked={
                  allSelected ? true : someSelected ? "indeterminate" : false
                }
                onCheckedChange={handleSelectAllChange}
              />
              <span className="text-xs font-medium">Select all</span>
            </div>

            {/*  permissions grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pr-3 -mr-3">
              {Object.entries(FEATURES).map(([featureKey, actions]) => {
                const featureTitle = titleCaseFromKey(featureKey);
                const enabledInFeature = Object.values(
                  features?.[featureKey] || {},
                ).filter(Boolean).length;

                return (
                  <div
                    key={featureKey}
                    className="border rounded-xl p-4 bg-background hover:bg-muted/20 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-foreground truncate">
                          {featureTitle}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Configure access for this module.
                        </p>
                      </div>

                      {/* Right controls */}
                      <div className="flex items-center gap-2 shrink-0">
                        {(() => {
                          const s = getFeatureSelectState(featureKey, actions);

                          return (
                            <>
                              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
                                <Checkbox
                                  checked={
                                    s.allSelected
                                      ? true
                                      : s.someSelected
                                        ? "indeterminate"
                                        : false
                                  }
                                  onCheckedChange={(v) => {
                                    const shouldSelectAll =
                                      v === true || v === "indeterminate";
                                    setAllForFeature(
                                      featureKey,
                                      actions,
                                      shouldSelectAll,
                                    );
                                  }}
                                />
                                Select all
                              </label>
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {Object.keys(actions).map((actionKey) => {
                        const checked = Boolean(
                          features?.[featureKey]?.[actionKey],
                        );
                        const disabled =
                          actionKey !== "view" && !features?.[featureKey]?.view;

                        return (
                          <label
                            key={actionKey}
                            className={[
                              "flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors",
                              checked
                                ? "border-primary/30 bg-primary/5"
                                : "border-border/60",
                              disabled
                                ? "opacity-60 cursor-not-allowed"
                                : "cursor-pointer hover:bg-muted/40",
                            ].join(" ")}
                          >
                            <Checkbox
                              checked={checked}
                              disabled={disabled}
                              onCheckedChange={() =>
                                toggleFeature(featureKey, actionKey)
                              }
                            />
                            <span className="leading-tight">
                              {FEATURE_LABELS?.[featureKey]?.[actionKey] ||
                                titleCaseFromKey(actionKey)}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="p-4 border-t flex items-center justify-between gap-3">
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Selected permissions: <b>{selectedCount}</b>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveRole} className="gap-2">
                <Shield className="h-4 w-4" />
                {mode === "edit" ? "Save Changes" : "Create Role"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ---------------- DELETE CONFIRM DIALOG ---------------- */}
      <Dialog
        open={deleteOpen}
        onOpenChange={(v) => {
          if (deleting) return;
          setDeleteOpen(v);
          if (!v) setRoleToDelete(null);
        }}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Role
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              You are about to delete{" "}
              <span className="font-semibold text-foreground">
                {roleToDelete?.name || "this role"}
              </span>
              . This action cannot be undone.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteRole}
              disabled={deleting}
              className="gap-2"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  Delete Role
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
